import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Table, TableHeader, TableBody, TableCell, TableHead, TableRow } from "./ui/table";
import { RefreshCw, Trash2, Check, X, Plus } from 'lucide-react';
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const GoogleSheetsManager = () => {
  const [sheets, setSheets] = useState([]);
  // Auth helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('gads_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
    };
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newSheetId, setNewSheetId] = useState('');
  const [newSheetName, setNewSheetName] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Lấy danh sách accounts
  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/accounts`);
      setAccounts(response.data.accounts);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Lấy danh sách Google Sheets
  const fetchSheets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sheets`);
      setSheets(response.data);
    } catch (err) {
      setError('Lỗi khi lấy danh sách Google Sheets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSheets();
  }, []);

  // Thêm Google Sheet mới
  const handleAddSheet = async () => {
    if (!newSheetId) {
      setError('Vui lòng nhập Google Sheet ID');
      return;
    }

    if (!newSheetName) {
      setError('Vui lòng nhập tên cho Google Sheet');
      return;
    }

    if (!selectedAccount) {
      setError('Vui lòng chọn tài khoản');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await axios.post(`${process.env.REACT_APP_API_URL}/api/sheets`, {
        google_sheet_id: newSheetId,
        name: newSheetName,
        account_id: selectedAccount
      });
      resetForm();
      fetchSheets();
    } catch (err) {
      setError(err.response?.data?.error || 'Lỗi khi thêm Google Sheet');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cập nhật trạng thái Google Sheet
  const handleStatusChange = async (sheetId, currentStatus) => {
    try {
      setLoading(true);
      setError(null);
      const newStatus = currentStatus === 'enable' ? 'disable' : 'enable';
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/sheets/${sheetId}/status`, { status: newStatus });
      fetchSheets();
    } catch (err) {
      setError(err.response?.data?.error || 'Lỗi khi cập nhật trạng thái');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Đồng bộ dữ liệu từ sheet
  const handleSyncSheet = async (sheetId, sheetTitle, accountId) => {
    try {
      setLoading(true);
      setError(null);
      await axios.post(`${process.env.REACT_APP_API_URL}/api/sheets/${sheetId}/sync`, {
        sheetTitle,
        accountId
      });
      setTimeout(() => {
        fetchSheets();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Lỗi khi đồng bộ dữ liệu');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Xóa Google Sheet
  const handleDeleteSheet = async (sheetId) => {
    try {
      setLoading(true);
      setError(null);
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/sheets/${sheetId}`);
      setDeleteConfirmId(null);
      fetchSheets();
    } catch (err) {
      setError(err.response?.data?.error || 'Lỗi khi xóa Google Sheet');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewSheetId('');
    setNewSheetName('');
    setSelectedAccount('');
    setShowAddForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-md font-bold">Quản lý Google Sheet</h2>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-2 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>            
            Thêm Sheet
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Thêm Google Sheet mới</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={resetForm}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sheetId">Google Sheet ID</Label>
                  <Input
                    id="sheetId"
                    value={newSheetId}
                    onChange={(e) => setNewSheetId(e.target.value)}
                    placeholder="Nhập Google Sheet ID"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sheetName">Tên Sheet</Label>
                  <Input
                    id="sheetName"
                    value={newSheetName}
                    onChange={(e) => setNewSheetName(e.target.value)}
                    placeholder="Nhập tên cho Google Sheet"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="account">Tài khoản</Label>
                <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                  <SelectTrigger id="account">
                    <SelectValue placeholder="Chọn tài khoản" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem key={account.customer_id} value={account.customer_id}>
                        {account.name} ({account.customer_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={resetForm}
                >
                  Hủy
                </Button>
                <Button
                  onClick={handleAddSheet}
                  disabled={loading || !newSheetId || !newSheetName || !selectedAccount}
                >
                  {loading ? 'Đang thêm...' : 'Thêm'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-md border overflow-x-auto bg-white">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow>
              <TableHead className="w-[200px]">ID</TableHead>
              <TableHead className="w-[200px]">Tên</TableHead>
              <TableHead className="w-[200px]">Tài khoản</TableHead>
              <TableHead className="w-[100px] text-center">Trạng thái</TableHead>
              <TableHead className="w-[150px]">Ngày tạo</TableHead>
              <TableHead className="w-[150px] text-center">Thao tác</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sheets.map((sheet) => (
              <TableRow key={sheet.id}>
                <TableCell>
                  <a
                    href={`https://docs.google.com/spreadsheets/d/${sheet.google_sheet_id}/edit`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {sheet.google_sheet_id}
                  </a>
                </TableCell>
                <TableCell>{sheet.name}</TableCell>
                <TableCell>
                  {accounts.find(acc => acc.customer_id === sheet.account_id)?.name || sheet.account_id}
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleStatusChange(sheet.id, sheet.status)}
                    className={sheet.status === 'enable'
                      ? 'h-6 w-6 bg-green-100 text-green-800 hover:bg-green-200'
                      : 'h-6 w-6 bg-red-100 text-red-800 hover:bg-red-200'}
                  >
                    {sheet.status === 'enable' ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  </Button>
                </TableCell>
                <TableCell>
                  {new Date(sheet.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSyncSheet(sheet.google_sheet_id, sheet.name, sheet.account_id)}
                      disabled={loading || sheet.status !== 'enable'}
                    >
                      Đồng bộ
                    </Button>
                    {deleteConfirmId === sheet.id ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteSheet(sheet.id)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-900"
                        >
                          Xác nhận
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteConfirmId(null)}
                          disabled={loading}
                        >
                          Hủy
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteConfirmId(sheet.id)}
                        disabled={loading}
                        className="h-6 w-6"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default GoogleSheetsManager;